import React, {useContext, useEffect, useState} from "react";
import styled from "styled-components";
import {Accordion, AccordionTab} from 'primereact/accordion';
import {apiRequest, formatDate, snakeToNormalCase, toast} from "@/Helpers/general";
import {Button} from "primereact/button";
import ContentLoadingTemplate from "@/Components/Templates/ContentLoadingTemplate";
import {CLAIM_RULESETS_ROWS_NO} from "@/Config/constants";
import {AppContext} from "@/Components/Context/AppContext";
import {CaseContext} from "@/Components/Context/CaseContext";
import {getFieldConfig, getFormErrorMessage, isFormFieldValid, validateForm} from "@/Helpers/forms";
import TextInputTemplate from "@/Components/Templates/Forms/TextInputTemplate";
import DateInputTemplate from "@/Components/Templates/Forms/DateInputTemplate";
import DropdownInputTemplate from "@/Components/Templates/Forms/DropdownInputTemplate";
import NumberInputTemplate from "@/Components/Templates/Forms/NumberInputTemplate";
import {caseFormConfig, fieldsPlaceholders} from "@/Config/forms";
import InfoMessageTemplate from "@/Components/Templates/InfoMessageTemplate";
import moment from "moment";
import CustomSelectTemplate from "@/Components/Templates/Forms/CustomSelectTemplate";
import {RequestsContext} from "@/Components/Context/RequestsContext";
import {Checkbox} from "primereact/checkbox";
import AddDateModal from "@/Components/Modals/AddDateModal";
import {Tooltip} from "primereact/tooltip";
import ConfirmDialogTemplate from "@/Components/Templates/ConfirmDialogTemplate";
import GtaRateSelect from "@/Components/Forms/Form/Selector/GtaRateSelect";

const RulesetsWrap = styled.div`
    .rulesets-wrap {
        min-height: 35vh;
        position: relative;
    }

    .rulesets-info-text {
        color: var(--dark-color);
    }

    .check-all-wrap {
        margin-left: 40px;

        > label {
            font-weight: 600;
            cursor: pointer;
        }
    }
`;

/**
 * Component related to the claim's rulesets
 */
const Rulesets = ({caseId, reloadCaseData, disabled = false}) => {
    const {toastRef} = useContext(AppContext);
    const {
        setInitialRulesetsFormData,
        setCurrentRulesetsFormData,
        saveRulesCategories,
        setShowTabSwitchConfirm,
        newTabEvent,
        setNewTabEvent,
        setActiveTabIndex,
        setCurrentTabName,
        setUpdateCurrentTabInHMenu,
        modalTabs,
        caseData
    } = useContext(CaseContext);
    const {chosOptions, gtaGroupsOptions} = useContext(RequestsContext);
    const booleanOptions = [
        {
            label: 'No',
            value: 0
        },
        {
            label: 'Yes',
            value: 1
        }
    ];

    const [rulesets, setRulesets] = useState([]);
    const [rulesetsLoading, setRulesetsLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeAccordionIndex, setActiveAccordionIndex] = useState([0]);
    const [formData, setFormData] = useState();
    const [formValidationRules, setFormValidationRules] = useState([]);
    const [formErrors, setFormErrors] = useState({});
    const [loadingStates, setLoadingStates] = useState();
    const [availableRulesNo, setAvailableRulesNo] = useState(0);
    const [totalRulesNo, setTotalRulesNo] = useState(0);
    const [percent, setPercent] = useState(0);
    const [visibleDateRanges, setVisibleDateRanges] = useState({});
    const [dropdownsOptions, setDropdownsOptions] = useState({});
    const [dropdownsOptionsApi, setDropdownsOptionsApi] = useState({});
    const [checkedBRulesets, setCheckedBRulesets] = useState([]);
    const [checkedRulesets, setCheckedRulesets] = useState([]);
    const [checkAllRulesets, setCheckAllRulesets] = useState(false);
    const [currentBRule, setCurrentBRule] = useState();
    const [showAddDateModal, setShowAddDateModal] = useState(false);
    const [showWarningModal, setShowWarningModal] = useState(false);

    const onGenerateOutcome = (ruleset = null) => {
        if (!!caseData.outcomes?.length) {
            setShowWarningModal(true);
        } else {
            if (ruleset) {
                runRuleset(ruleset)
            } else {
                submitForm()
            }
        }
    }
    /**
     * Run the rulesets
     */
    const submitForm = async () => {
        setShowWarningModal(false)
        setFormLoading(true);

        let requestParams = {};

        requestParams.rulesets = checkedRulesets;

        if (checkedBRulesets?.length > 0) {
            requestParams.bRulesets = checkedBRulesets;
        }

        apiRequest('post', 'case/' + caseId + '/rulesets', {
            data: requestParams,
            onSuccess: (response) => {
                setFormLoading(false);

                setRulesets([]);
                loadRulesets();

                toast(toastRef, 'success', 'The rulesets ran successfully.');
                reloadCaseData();
            },
            onFailure: (error) => {
                setFormLoading(false);

                if (error.status == 422) {
                    if (error.messages) {
                        let errorMessages = error.messages,
                            newFormErrors = {};

                        for (let key in errorMessages) {
                            if (Object.hasOwnProperty.call(errorMessages, key)) {
                                newFormErrors[key] = errorMessages[key];
                            }
                        }

                        toast(toastRef, 'error', 'Error running rulesets.');

                    } else {
                        toast(toastRef, 'error', 'Error running rulesets.');
                    }

                } else if (error.status == 403) {
                    toast(toastRef, 'error', 'Error: the case needs to be assigned in order to save data.');

                } else {
                    toast(toastRef, 'error', 'Error running rulesets.');
                }
            }
        });
    };

    /**
     * Check/uncheck a B ruleset
     *
     * @param {object} e object with event data
     */
    const onCheckBRuleset = (e) => {
        let copyCheckedBRulesets = [...checkedBRulesets],
            copyCheckedRulesets = [...checkedRulesets];

        if (e.checked) {
            let searchRuleset = copyCheckedRulesets.includes(e.value);

            copyCheckedBRulesets.push(e.value);

            // if the ruleset is not checked, check it
            if (!searchRuleset) {
                copyCheckedRulesets.push(e.value);
                setCheckedRulesets(copyCheckedRulesets);
            }

        } else {
            copyCheckedBRulesets = copyCheckedBRulesets.filter(rule => rule != e.value);
        }

        setCheckedBRulesets(copyCheckedBRulesets);
    }

    /**
     * Check/uncheck a ruleset
     *
     * @param {object} e object with event data
     */
    const onCheckRuleset = (e) => {
        let copyCheckedRulesets = [...checkedRulesets];

        if (e.checked) {
            copyCheckedRulesets.push(e.value);

            // check if all the rules are checked
            let allRulesChecked = true;

            rulesets.forEach((rule) => {
                if (!checkedRulesets.includes(rule.id) && rule.id != e.value) {
                    allRulesChecked = false;
                }
            });

            setCheckAllRulesets(allRulesChecked);

        } else {
            copyCheckedRulesets = copyCheckedRulesets.filter(rule => rule != e.value);

            setCheckAllRulesets(false);
        }

        setCheckedRulesets(copyCheckedRulesets);
    }

    /**
     * Check/uncheck all rulesets
     *
     * @param {object} e object with event data
     */
    const onCheckAllRulesets = (e) => {
        if (e.checked) {
            let checkRulesets = [];

            rulesets.forEach((rule) => {
                checkRulesets.push(rule.id);
            });

            setCheckedRulesets(checkRulesets);

        } else {
            setCheckedRulesets([]);
        }

        setCheckAllRulesets(Boolean(e.checked));
    };

    /**
     * Accordion header
     *
     * @param {object} ruleset data related to the ruleset
     */
    const headerTemplate = (ruleset) => {
        const isGeneralRule = ruleset.label?.includes('GENERAL')
        return (
            <div className="flex w-full justify-content-between">
                <div>
                    {
                        isGeneralRule ? undefined :
                            <Checkbox inputId={'check-ruleset-' + ruleset.id} className="mr-2"
                                      name="checked_rulesets"
                                      value={ruleset.id}
                                      onChange={onCheckRuleset}
                                      checked={checkedRulesets.indexOf(ruleset.id) !== -1}
                            />
                    }

                    <label htmlFor={'check-ruleset-' + ruleset.id} className="ruleset-name">{ruleset.name}</label>
                </div>

                <div className="flex">
                    {ruleset.type == 'b-rule' && <div className="text-warning mr-3">
                        {/* <i className="pi mr-2 pi-info-circle"></i> */}
                        <span>Alternative options available</span>
                    </div>}

                    {ruleset.type == 'c-rule' && <div className="text-info mr-3">
                        {/* <i className="pi mr-2 pi-question-circle"></i> */}
                        <span>Data required</span>
                    </div>}
                    {
                        isGeneralRule ? undefined :
                            <div className={ruleset.valid ? 'text-success' : 'text-error'}>
                                <i className={'pi mr-2 ' + (ruleset.valid ? 'pi-check-circle' : 'pi-times-circle')}></i>
                                <span>{ruleset.valid ? 'Available' : 'N/A'}</span>
                            </div>
                    }
                </div>
            </div>
        );
    };

    /**
     * Update the state of the form data (when changing an input value)
     *
     * @param {string} name the name of the input
     * @param {string} value the new value of the input
     * @param {string} category from where the input belongs to
     */
    const changeFormData = (name, value, category) => {
        let getFormData = {...formData},
            getTab = name.match(/(.*?)(?=\[)/)[1],
            getName = name.match(/\[([^[\]]+)\]/)[1],
            fieldsConfig = caseFormConfig.tabs.find(tab => tab.key == getTab)?.fields,
            allFields = [];

        fieldsConfig.forEach((config) => {
            allFields = allFields.concat(config.fields);
        });

        let fieldConfig = allFields.find(field => field.name == getName);

        if (fieldConfig) {
            // check if this field is a dependency source
            if (fieldConfig.dependencySource && value != fieldConfig.dependencyMatchValue) {
                allFields.forEach((field) => {
                    // if dependent fields will be hidden, clear their values
                    if (field.dependency?.type === 'visibility' && field.dependency.sourceField === getName) {
                        changeFormData(getTab + '[' + field.name + ']', '', category);
                    }
                });
            }

            if (value === null) {
                let totalDays = 0;
                if (fieldConfig.type == 'date_range_start' && fieldConfig.calculate?.type == 'days') {
                    getFormData[category][getTab + "[" + fieldConfig.calculate?.targetField + "]"] = 0
                    allFields.forEach((item) => {
                        if (item.name != fieldConfig.calculate?.targetField
                            && item?.calculate?.group == fieldConfig?.dateRange?.key
                            && getFormData[category][item.name]) {
                            totalDays += parseInt(getFormData[category][item.name]);
                        }
                    });

                    getFormData[category][fieldConfig.calculate?.totalField] = totalDays;
                }
            }
            if (fieldConfig.calculate) {
                // check if this fields is a date range and calculate the number of days selected
                if (fieldConfig.type == 'date_range_start' && fieldConfig.calculate?.type == 'days') {
                    if (Array.isArray(value) && value[0] && value[1]) {
                        let startDate = moment(value[0]),
                            endDate = moment(value[1]),
                            daysBetween,
                            totalDays = 0;

                        if (startDate.isValid() && endDate.isValid()) {
                            daysBetween = startDate.diff(endDate, 'days');
                        }

                        if ((daysBetween || daysBetween === 0) && fieldConfig.calculate?.targetField) {

                            getFormData[category][getTab + "[" + fieldConfig.calculate?.targetField + "]"] = Math.abs(daysBetween) + 1;

                            totalDays += Math.abs(daysBetween) + 1;
                        }

                        if (fieldConfig.calculate?.extraCalculate?.type === 'daysOver') {
                            getFormData[category][getTab + "[" + fieldConfig.calculate?.extraCalculate?.targetField + "]"] = totalDays > fieldConfig.calculate?.extraCalculate?.value ? 1 : 0;
                        }

                        // go thru each number of days field and calculate the total
                        allFields.forEach((item) => {
                            if (item.name != fieldConfig.calculate?.targetField
                                && item?.calculate?.group == fieldConfig?.dateRange?.key
                                && getFormData[category][item.name]) {
                                totalDays += parseInt(getFormData[category][item.name]);
                            }
                        });

                        getFormData[category][fieldConfig.calculate?.totalField] = totalDays;
                    }
                }

                // // check if this is a number of days field and update the total amount of days field
                // if (fieldConfig.calculate?.type == 'days' && fieldConfig.calculate?.group) {
                //     // go thru each number of days field and update the total
                //     let totalDaysNo = 0;

                //     allFields.forEach((fieldItem) => {
                //         if (fieldItem.name == fieldConfig.name) {
                //             totalDaysNo += value;

                //         } else {
                //             if (fieldItem?.calculate?.group == fieldConfig?.calculate?.group
                //                 && getFormData[category][fieldItem.name]) {
                //                 totalDaysNo += parseInt(getFormData[category][fieldItem.name]);
                //             }
                //         }
                //     });

                //     getFormData[category][fieldConfig.calculate?.totalField] = totalDaysNo;
                // }
            }

            // check if this field (MOT or TAX) should update the value of another field
            if (fieldConfig.changesAnotherFieldValue) {
                // let compareField = caseData[fieldConfig.changesAnotherFieldValue.compareField.category][fieldConfig.changesAnotherFieldValue.compareField.field];
                let compareFieldConfig = getFieldConfig(fieldConfig.changesAnotherFieldValue.compareField.category, fieldConfig.changesAnotherFieldValue.compareField.field),
                    compareFieldName = compareFieldConfig.type == 'date_range_end' ? compareFieldConfig.startKey : fieldConfig.changesAnotherFieldValue.compareField.field,
                    compareField = getFormData[category][fieldConfig.changesAnotherFieldValue.compareField.category + '[' + compareFieldName + ']'],
                    compareFieldValue = compareFieldConfig.type == 'date_range_end' && compareField?.length > 1 ? compareField[1] : compareField;

                if (value && compareFieldValue) {
                    // check date after
                    if (fieldConfig.changesAnotherFieldValue.compare.type == 'dateAfter') {
                        getFormData[category][getTab + '[' + fieldConfig.changesAnotherFieldValue.targetField + ']'] = moment(value).isAfter(compareFieldValue)
                            ? fieldConfig.changesAnotherFieldValue.compare.trueValue
                            : fieldConfig.changesAnotherFieldValue.compare.falseValue;
                    }

                    // check date before
                    if (fieldConfig.changesAnotherFieldValue.compare.type == 'dateBefore') {
                        getFormData[category][getTab + '[' + fieldConfig.changesAnotherFieldValue.targetField + ']'] = moment(value).isBefore(compareFieldValue)
                            ? fieldConfig.changesAnotherFieldValue.compare.trueValue
                            : fieldConfig.changesAnotherFieldValue.compare.falseValue;
                    }

                    // check year after
                    if (fieldConfig.changesAnotherFieldValue.compare.type == 'yearAfter') {
                        getFormData[category][getTab + '[' + fieldConfig.changesAnotherFieldValue.targetField + ']'] = moment(value).isAfter(moment(compareFieldValue).add(1, 'years')) ? 0 : 1;
                    }

                    // check year before
                    if (fieldConfig.changesAnotherFieldValue.compare.type == 'yearBefore') {
                        getFormData[category][getTab + '[' + fieldConfig.changesAnotherFieldValue.targetField + ']'] = moment(value).isBefore(moment(compareFieldValue).subtract(1, 'years')) ? 0 : 1;
                    }
                }
            }

            // check if this field (hire dates or accident date) should update the values of another fields
            if (fieldConfig.changesOtherFieldsValues) {
                let searchFieldName = fieldConfig.type == 'date_range_start' ? fieldConfig.endKey : fieldConfig.name,
                    sValue = fieldConfig.type == 'date_range_start' && value?.length > 1 ? value[1] : value;

                caseFormConfig.tabs.forEach((tab) => {
                    tab?.fields.forEach((sCategory) => {
                        sCategory?.fields.forEach((sField) => {
                            if (sField.changesAnotherFieldValue && sField.changesAnotherFieldValue.compareField.field == searchFieldName) {

                                let compareField = getFormData[category][tab.key + '[' + sField.name + ']'];

                                if (value && compareField) {
                                    // check date after
                                    if (sField.changesAnotherFieldValue.compare.type == 'dateAfter') {
                                        getFormData[category][tab.key + '[' + sField.changesAnotherFieldValue.targetField + ']'] = moment(compareField).isAfter(sValue)
                                            ? sField.changesAnotherFieldValue.compare.trueValue
                                            : sField.changesAnotherFieldValue.compare.falseValue;
                                    }

                                    // check date before
                                    if (sField.changesAnotherFieldValue.compare.type == 'dateBefore') {
                                        getFormData[category][tab.key + '[' + sField.changesAnotherFieldValue.targetField + ']'] = moment(compareField).isBefore(sValue)
                                            ? sField.changesAnotherFieldValue.compare.trueValue
                                            : sField.changesAnotherFieldValue.compare.falseValue;
                                    }

                                    // check year after
                                    if (sField.changesAnotherFieldValue.compare.type == 'yearAfter') {
                                        getFormData[category][tab.key + '[' + sField.changesAnotherFieldValue.targetField + ']'] = moment(compareField).isAfter(moment(sValue).add(1, 'years')) ? 0 : 1;
                                    }

                                    // check year before
                                    if (sField.changesAnotherFieldValue.compare.type == 'yearBefore') {
                                        getFormData[category][tab.key + '[' + sField.changesAnotherFieldValue.targetField + ']'] = moment(compareField).isBefore(moment(sValue).subtract(1, 'years')) ? 0 : 1;
                                    }
                                }
                            }
                        });
                    });
                });
            }
        }

        getFormData[category][name] = value;

        setFormData(getFormData);
        setCurrentRulesetsFormData(getFormData);
    };

    /**
     * Update the state of the form loaders
     *
     * @param {string} name the name of the ruleset
     * @param {string} type the type of loader (save or runRuleset)
     * @param {boolean} value the new value of the loader
     */
    const changeLoadingStates = (name, type, value) => {
        let getLoadingStates = {...loadingStates};

        getLoadingStates[name] = {
            save: type == 'save' ? value : false,
            runRuleset: type == 'runRuleset' ? value : false
        };

        setLoadingStates(getLoadingStates);
    };

    /**
     * Save ruleset fields
     *
     * @param {object} ruleset data about the ruleset
     */
    const saveFields = (ruleset) => {
        if (formData && formData[ruleset.name]) {
            let validationRules = {...formValidationRules},
                copyFormData = {...formData[ruleset.name]};

            // parse date ranges
            ruleset?.fill_fields.forEach((rulesetField) => {
                let fieldsConfig = caseFormConfig.tabs.find(tab => tab.key == rulesetField.relation)?.fields,
                    allFields = [];

                fieldsConfig.forEach((config) => {
                    allFields = allFields.concat(config.fields);
                });

                let fieldConfig = allFields.find(field => field.name == rulesetField.field);

                if (fieldConfig) {
                    // date ranges
                    if (fieldConfig.type == 'date_range_start') {
                        let fieldValue = copyFormData[rulesetField.relation + '[' + rulesetField.field + ']'];

                        if (Array.isArray(fieldValue)) {
                            let copyDatesArray = [...fieldValue];

                            if (copyDatesArray[0]) {
                                copyFormData[rulesetField.relation + '[' + rulesetField.field + ']'] = copyDatesArray[0];
                            }

                            if (copyDatesArray[1] && typeof (copyFormData[rulesetField.relation + '[' + fieldConfig.endKey + ']']) != 'undefined') {
                                copyFormData[rulesetField.relation + '[' + fieldConfig.endKey + ']'] = copyDatesArray[1];
                            }
                        }
                    }
                }
            });

            // remove the associated field validation in case the B rule option is checked for this ruleset
            if (checkedBRulesets.includes(ruleset.id)) {
                validationRules[ruleset.name] = validationRules[ruleset.name].filter((rule) => rule.field != ruleset?.type_suggestion?.field);
            }

            changeLoadingStates(ruleset.name, 'save', true);

            let validation = validateForm(validationRules[ruleset.name], copyFormData, setFormErrors, ruleset.name, true);

            if (validation) {
                setFormLoading(true);
                apiRequest('post', 'case/' + caseId + '/ruleset-fields/' + ruleset.id, {
                    data: copyFormData,
                    onSuccess: (response) => {
                        setFormLoading(false);
                        changeLoadingStates(ruleset.name, 'save', false);

                        if (response?.data?.responseType == 'success') {
                            setRulesets([]);
                            loadRulesets();

                            toast(toastRef, 'success', 'The required ruleset fields have been saved.');

                            reloadCaseData();
                        }
                    },
                    onFailure: (error) => {
                        setFormLoading(false);
                        changeLoadingStates(ruleset.name, 'save', false);

                        if (error.status == 422) {
                            if (error.messages) {
                                let errorMessages = error.messages,
                                    newFormErrors = {};

                                for (let key in errorMessages) {
                                    if (Object.hasOwnProperty.call(errorMessages, key)) {
                                        newFormErrors[key] = errorMessages[key];
                                    }
                                }

                                setFormErrors(newFormErrors);

                            } else {
                                toast(toastRef, 'error', 'Error saving ruleset fields.');
                            }

                        } else if (error.status == 403) {
                            toast(toastRef, 'error', 'Error: the case needs to be assigned in order to save data.');

                        } else {
                            toast(toastRef, 'error', 'Error saving ruleset fields.');
                        }
                    }
                });

            } else {
                changeLoadingStates(ruleset.name, 'save', false);
            }
        }
    };

    /**
     * Run a specific ruleset
     *
     * @param {object} ruleset data about the ruleset
     */
    const runRuleset = (ruleset) => {
        setFormLoading(true);
        let requestData = checkedBRulesets.includes(ruleset.id) ? {useBrule: true} : {};

        changeLoadingStates(ruleset.name, 'runRuleset', true);

        apiRequest('post', 'case/' + caseId + '/ruleset/' + ruleset.id, {
            data: requestData,
            onSuccess: (response) => {
                setFormLoading(false);
                changeLoadingStates(ruleset.name, 'runRuleset', false);

                if (response?.data?.responseType == 'success') {
                    setRulesets([]);
                    loadRulesets();

                    toast(toastRef, 'success', 'The ruleset ran successfully.');
                    reloadCaseData();
                }
            },
            onFailure: (error) => {
                setFormLoading(false);
                changeLoadingStates(ruleset.name, 'runRuleset', false);

                if (error.status == 422) {
                    if (error.messages) {
                        let errorMessages = error.messages,
                            newFormErrors = {};

                        for (let key in errorMessages) {
                            if (Object.hasOwnProperty.call(errorMessages, key)) {
                                newFormErrors[key] = errorMessages[key];
                            }
                        }

                        setFormErrors(newFormErrors);

                    } else {
                        toast(toastRef, 'error', 'Error running ruleset.');
                    }

                } else if (error.status == 403) {
                    toast(toastRef, 'error', 'Error: the case needs to be assigned in order to save data.');

                } else {
                    toast(toastRef, 'error', 'Error running ruleset.');
                }
            }
        });
    };

    /**
     * Make another date range field visible
     *
     * @param {object} field object with field data
     */
    const addDateRange = (field) => {
        let copyVisibleDateRanges = {...visibleDateRanges};

        copyVisibleDateRanges[field.dateRange.key] = field.dateRange.index;

        setVisibleDateRanges(copyVisibleDateRanges);
    };

    /**
     * Generate form field
     *
     * @param {string} rulesetName the name of the ruleset
     * @param {object} fieldConfig data related to the form field
     */
    const generateInputField = (rulesetName, fieldConfig, fieldRelation, fieldType) => {
        let fieldName = fieldRelation + '[' + fieldConfig.name + ']',
            fieldLabel = <span>
                {fieldType == 'required' &&
                    <span className="text-error required-field-notice">*</span>}{fieldConfig.label}
            </span>;

        switch (fieldConfig.type) {
            case 'text':
                return (
                    <TextInputTemplate
                        name={fieldName}
                        label={fieldLabel}
                        placeholder={fieldsPlaceholders.text}
                        value={formData && formData[rulesetName] ? (formData[rulesetName][fieldName] || '') : ''}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, fieldName)}
                        errorMessage={getFormErrorMessage(formErrors, fieldName)}
                        category={rulesetName}
                        disabled={disabled || fieldConfig.disabled}
                    />
                );

            case 'date':
                return (
                    <DateInputTemplate
                        name={fieldName}
                        label={fieldLabel}
                        placeholder={fieldsPlaceholders.date}
                        value={formData && formData[rulesetName] ? (formData[rulesetName][fieldName]) || '' : ''}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, fieldName)}
                        errorMessage={getFormErrorMessage(formErrors, fieldName)}
                        category={rulesetName}
                        disabled={disabled || fieldConfig.disabled}
                    />
                );

            case 'date_range_start':
                return (<>
                    {(fieldConfig.dateRange.index == 0
                            || (fieldConfig.dateRange.index <= visibleDateRanges[fieldConfig.dateRange.key])
                            || (formData && formData[rulesetName] && formData[rulesetName][fieldName])) &&
                        <DateInputTemplate
                            name={fieldName}
                            label={fieldLabel}
                            placeholder={fieldConfig.custom_placeholder || fieldsPlaceholders.dateRange}
                            value={formData && formData[rulesetName] ? (formData[rulesetName][fieldName]) || '' : ''}
                            bottomMargin={3}
                            onChangeFunction={changeFormData}
                            isValid={isFormFieldValid(formErrors, fieldName)}
                            errorMessage={getFormErrorMessage(formErrors, fieldName)}
                            category={rulesetName}
                            disabled={disabled || fieldConfig.disabled}
                            selectionMode="range"
                        />}
                    {fieldConfig.dateRange.index == visibleDateRanges[fieldConfig.dateRange.key] + 1 &&
                        <div className="mb-3 relative">
                            <label htmlFor={fieldName + '-add-btn'}
                                   className="w-full block mb-2 pr-4 one-line">&nbsp;</label>
                            <Button className="grey-nav-btn grey-nav-btn-hover" label="Add date range"
                                    onClick={() => addDateRange(fieldConfig)} icon="pi pi-plus"/>
                        </div>}
                </>);
                break;

            case 'boolean':
            case 'dropdown':
                return (
                    <DropdownInputTemplate
                        name={fieldName}
                        label={fieldLabel}
                        placeholder={fieldsPlaceholders.boolean}
                        value={formData && formData[rulesetName] ? (formData[rulesetName][fieldName]) : ''}
                        options={fieldConfig.type == 'dropdown'
                            ?
                            (fieldConfig.dropdownOptionsAPI && dropdownsOptionsApi && dropdownsOptionsApi[fieldConfig.dropdownOptionsAPI]
                                ? dropdownsOptionsApi[fieldConfig.dropdownOptionsAPI]
                                : (fieldConfig.dropdownOptions && dropdownsOptions && dropdownsOptions[fieldConfig.name]
                                    ? dropdownsOptions[fieldConfig.name]
                                    : []))
                            : booleanOptions}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, fieldName)}
                        errorMessage={getFormErrorMessage(formErrors, fieldName)}
                        category={rulesetName}
                        disabled={disabled || fieldConfig.disabled || (fieldConfig.name == 'cho_id' && formData && formData[rulesetName]['claimant_reps[cho_id]'] != '')}
                    />
                );

            case 'integer':
                return (
                    <NumberInputTemplate
                        name={fieldName}
                        label={fieldLabel}
                        placeholder={fieldsPlaceholders.integer}
                        value={(formData && typeof formData[rulesetName][fieldName] !== 'undefined') ? formData[rulesetName][fieldName] : null}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, fieldName)}
                        errorMessage={getFormErrorMessage(formErrors, fieldName)}
                        category={rulesetName}
                        disabled={disabled || fieldConfig.disabled}
                    />
                );

            case 'float':
                return (
                    <NumberInputTemplate
                        name={fieldName}
                        label={fieldLabel}
                        placeholder={fieldsPlaceholders.integer}
                        labelClassName="one-line"
                        value={(formData && typeof formData[rulesetName][fieldName] !== 'undefined') ? formData[rulesetName][fieldName] : null}
                        mode={'decimal'}
                        bottomMargin={3}
                        format
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, fieldName)}
                        errorMessage={getFormErrorMessage(formErrors, fieldName)}
                        category={rulesetName}
                        disabled={disabled || fieldConfig.disabled}
                    />
                );

            case 'custom-select':
                return (
                    <CustomSelectTemplate
                        name={fieldName}
                        label={fieldLabel}
                        placeholder={fieldConfig.custom_placeholder || fieldsPlaceholders.boolean}
                        labelClassName="one-line"
                        value={formData && formData[rulesetName] ? (formData[rulesetName][fieldName]) : ''}
                        options={fieldConfig.customOptions}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, fieldName)}
                        errorMessage={getFormErrorMessage(formErrors, fieldName)}
                        category={rulesetName}
                        disabled={disabled || fieldConfig.disabled}
                    />
                );

            case 'gta_rate':
                return <GtaRateSelect
                    name={fieldName}
                    label={fieldLabel}
                    placeholder={fieldConfig.custom_placeholder || fieldsPlaceholders.dropdown}
                    value={formData && formData[rulesetName] ? (formData[rulesetName][fieldName]) : ''}
                    errorMessage={getFormErrorMessage(formErrors, fieldName)}
                    pt={{dropdown: {className: 'w-full'}}}
                    onChange={(e) => changeFormData(fieldName, e.value, rulesetName)}
                />

            default:
                break;
        }
    };

    /**
     * Check if a field wrapper and it's contents can be shown or not
     *
     * @param {object} tab object with tab data
     * @param {object} fields object with field data
     */
    const canShowField = (rulesetName, field, fieldRelation) => {
        let fieldName = fieldRelation + '[' + field.name + ']';

        return field.type != 'date_range_end' && field.type != 'date_range_start' && !field.group // fields which are not date range
            // fields which are date range
            || (field.type == 'date_range_start'
                && (field.dateRange.index <= parseInt(visibleDateRanges[field.dateRange.key]) + 1 || (formData && formData[rulesetName][fieldName]))
            )
            // fields which are in the same group
            || (field.group
                && (field.group.index <= visibleDateRanges[field.group.key])
            );
    };

    /**
     * Accordion content
     *
     * @param {object} ruleset data related to the ruleset
     */
    const generateAccordionContent = (ruleset) => {
        const isGeneralRule = ruleset.label?.includes('GENERAL')
        let allFieldsOptional = true,
            notificationMessage = '';

        if (ruleset?.fill_fields?.length > 0) {
            ruleset.fill_fields.forEach((field) => {
                if (field.type == 'required' || field.type == 'required_if') {
                    allFieldsOptional = false;
                }
            });
        }

        // set notification message for b or c type rulesets
        if (ruleset.type) {
            if (ruleset.type == 'c-rule') {
                notificationMessage = ruleset.type_message;

            } else {
                if (ruleset.type_suggestion) {
                    let missingDate = ruleset.type_suggestion?.label ? (snakeToNormalCase(ruleset.type_suggestion.label) + ' is missing. ') : '',
                        suggestedDate = ruleset.type_suggestion?.value ? ('(' + formatDate(ruleset.type_suggestion?.value, true) + ')') : '';

                    notificationMessage = <div className="flex align-items-center">
                        {ruleset.type_suggestion?.message &&
                            <Checkbox inputId={'check-alternative-data-' + ruleset.id} className="mr-2"
                                      name="checked_B_rulesets"
                                      value={ruleset.id}
                                      onChange={onCheckBRuleset}
                                      checked={checkedBRulesets.indexOf(ruleset.id) !== -1}
                            />}

                        <label htmlFor={'check-alternative-data-' + ruleset.id} className="ruleset-name">
                            {ruleset.type_suggestion?.message
                                ? missingDate + ruleset.type_suggestion.message + ' ' + suggestedDate
                                : missingDate
                            }
                        </label>

                        {!ruleset.type_suggestion?.message &&
                            <Button className="ml-3 p-button-sm p-button-warning" label="Use another date"
                                    onClick={() => {
                                        setCurrentBRule(ruleset);
                                        setShowAddDateModal(true);
                                    }}/>}
                    </div>;
                }
            }
        }

        return (
            <div>
                {notificationMessage != '' &&
                    <InfoMessageTemplate className="no-icon" type={ruleset.type == 'b-rule' ? 'warn' : 'info'}
                                         message={notificationMessage}/>}

                {ruleset.description && <InfoMessageTemplate type="neutral" innerHtml={ruleset.description}/>}

                <div>
                    {ruleset.description && <div className="mt-4">
                        <span className="font-bold">Fields which need to be filled:</span>
                        <hr className="mt-2 mb-3"/>
                    </div>}

                    <div className="general-form relative">
                        <div className="grid">
                            {ruleset.fill_fields.map((field) => {
                                let fieldConfig = getFieldConfig(field.relation, field.field);

                                if (fieldConfig && canShowField(ruleset.name, fieldConfig, field.relation)) {
                                    return (
                                        <div className="col-12 md:col-6 lg:col-4 relative"
                                             key={ruleset.name + '_' + field.label}>
                                            {generateInputField(ruleset.name, fieldConfig, field.relation, field.type)}
                                        </div>
                                    );
                                }
                            })}
                        </div>

                        <Tooltip target=".required-field-notice" content="Required field" position="top"/>

                        {loadingStates
                            && (loadingStates[ruleset.name].save || loadingStates[ruleset.name].runRuleset)
                            && <ContentLoadingTemplate/>}
                    </div>
                </div>

                <hr/>
                {!disabled && (
                    <div className="text-right mb-2">
                        <Button label="Save" className="mr-3"
                                onClick={() => saveFields(ruleset)}
                                loading={(loadingStates && loadingStates[ruleset.name].save) || formLoading}
                                loadingIcon="pi pi-spin pi-sun"
                                iconPos="center"
                        />
                        {!isGeneralRule && ruleset.valid && <Button label="Run Ruleset"
                                                                    onClick={() => onGenerateOutcome(ruleset)}
                                                                    loading={(loadingStates && loadingStates[ruleset.name].runRuleset) || formLoading}
                                                                    loadingIcon="pi pi-spin pi-sun"
                                                                    iconPos="center"
                        />}
                    </div>)}
            </div>
        );
    };

    /**
     * Get rulesets from the API
     */
    const loadRulesets = async () => {
        setRulesetsLoading(true);
        setFormData();

        let requestParams = {
            page: currentPage,
            limit: CLAIM_RULESETS_ROWS_NO
        };

        apiRequest('get', 'case/' + caseId + '/rulesets', {
            data: requestParams,
            onSuccess: (response) => {
                setRulesetsLoading(false);

                if (response.result?.ruleset_run_all_percent) {
                    setPercent(response.result.ruleset_run_all_percent);
                }

                if (response.result?.rulesets && response.result.rulesets.length > 0) {
                    let rulesetsFormData = {},
                        initialRulesetsFormData = {},
                        initLoadingStates = {},
                        validationRules = {},
                        availableRules = 0,
                        dateRanges = {},
                        copyDropdownsOptions = {...dropdownsOptions};

                    response.result.rulesets.forEach((ruleset) => {
                        rulesetsFormData[ruleset.name] = {};
                        initialRulesetsFormData[ruleset.name] = {};
                        initLoadingStates[ruleset.name] = {
                            save: false,
                            runRuleset: false
                        };
                        validationRules[ruleset.name] = [];

                        if (ruleset.fill_fields && ruleset.fill_fields?.length > 0) {
                            ruleset.fill_fields.forEach((field) => {
                                let fieldConfig = getFieldConfig(field.relation, field.field),
                                    fieldValue = field.value;

                                if (fieldConfig) {
                                    // set visible date range fields
                                    if (fieldConfig.type == 'date_range_start') {
                                        if (typeof (dateRanges[fieldConfig.dateRange.key]) == 'undefined') {
                                            dateRanges[fieldConfig.dateRange.key] = 0; // here we will save the index of the last visible date range from the group
                                        }

                                        if (fieldValue) {
                                            dateRanges[fieldConfig.dateRange.key] = fieldConfig.dateRange.index;
                                        }
                                    }

                                    // date ranges
                                    if (fieldConfig.type == 'date_range_start') {
                                        let getStartDate = moment(fieldValue);

                                        if (getStartDate.isValid()) {
                                            fieldValue = [getStartDate.toDate()];
                                        }
                                    }

                                    if (fieldConfig.type == 'date_range_end' && Array.isArray(rulesetsFormData[ruleset.name][field.relation + '[' + fieldConfig.startKey + ']'])) {
                                        let getEndDate = moment(fieldValue);

                                        if (getEndDate.isValid()) {
                                            rulesetsFormData[ruleset.name][field.relation + '[' + fieldConfig.startKey + ']'].push(getEndDate.toDate());
                                            initialRulesetsFormData[ruleset.name][field.relation + '[' + fieldConfig.startKey + ']'].push(getEndDate.toDate());
                                        }
                                    }

                                    // dates
                                    if (fieldConfig.type == 'date') {
                                        let getDate = moment(fieldValue);

                                        if (getDate.isValid()) {
                                            fieldValue = getDate.toDate();
                                        }
                                    }

                                    // booleans
                                    if (fieldConfig.type == 'boolean') {
                                        switch (fieldValue) {
                                            case true:
                                                fieldValue = 1;
                                                break;

                                            case false:
                                                fieldValue = 0;
                                                break;

                                            case null:
                                                fieldValue = '';
                                                break;

                                            default:
                                                fieldValue = '';
                                                break;
                                        }
                                    }

                                    rulesetsFormData[ruleset.name][field.relation + '[' + field.field + ']'] = fieldValue;
                                    initialRulesetsFormData[ruleset.name][field.relation + '[' + field.field + ']'] = fieldValue;

                                    // set validation rules
                                    if (field.type == 'required' || field.type == 'required_if') {
                                        let checkRequired = fieldConfig?.validation?.length > 0 && fieldConfig.validation.find((item) => item.name == 'required'),
                                            vRules = checkRequired ? [] : [{name: 'required'}];

                                        validationRules[ruleset.name].push({
                                            field: fieldConfig.name,
                                            rules: fieldConfig?.validation?.length > 0 ? fieldConfig.validation.concat(vRules) : vRules,
                                            category: field.relation,
                                        });
                                    }

                                    // set dropdown options from the config
                                    if (fieldConfig.dropdownOptions) {
                                        copyDropdownsOptions[fieldConfig.name] = fieldConfig.dropdownOptions;
                                    }

                                }
                            });
                        }

                        if (ruleset.valid) {
                            availableRules++;
                        }
                    });

                    setDropdownsOptions((previousState) => {
                        setDropdownsOptions({...previousState, ...copyDropdownsOptions});
                    });
                    setFormData(rulesetsFormData);
                    setInitialRulesetsFormData(initialRulesetsFormData);
                    setCurrentRulesetsFormData();
                    setLoadingStates(initLoadingStates);
                    setRulesets(response.result.rulesets);
                    setFormValidationRules(validationRules);
                    setAvailableRulesNo(availableRules);
                    setTotalRulesNo(response.result.rulesets.length);
                    setVisibleDateRanges(dateRanges);

                    // check available rulesets
                    let copyCheckedRulesets = [...checkedRulesets],
                        copyCheckedBRulesets = [...checkedBRulesets],
                        excludedRulesetsToCheck: number[] = [];

                    response.result.rulesets.forEach((rule) => {
                        if (rule.valid) {
                            copyCheckedRulesets.push(rule.id);
                        }

                        if (['ARBITRATE_BHR', 'ARBITRATE_BHR_REQUEST_OUTCOME_HIRE_VEHICLE'].includes(rule.label)) {
                            excludedRulesetsToCheck.push(rule.id);
                        }

                        // check if the b-rule still exists, otherwise remove it
                        if (copyCheckedBRulesets.includes(rule.id) && rule?.type != 'b-rule') {
                            copyCheckedBRulesets = copyCheckedBRulesets.filter((id) => id != rule.id);
                        }
                    });

                    if (response.result.arbitrate_bhr_rule_unchecked) {
                        copyCheckedRulesets = copyCheckedRulesets.filter((id => !excludedRulesetsToCheck.includes(id)))
                    }

                    setCheckedRulesets(copyCheckedRulesets);
                    setCheckedBRulesets(copyCheckedBRulesets);
                }
            },
            onFailure: () => {
                setRulesetsLoading(false);
            }
        });
    };

    // init
    useEffect(() => {
        loadRulesets();
        setCurrentRulesetsFormData();
    }, []);

    useEffect(() => {
        let copyDropdownsOptionsApi = {...dropdownsOptionsApi};

        caseFormConfig.tabs.forEach((tab) => {
            tab.fields.forEach((category) => {
                category.fields.forEach((field) => {
                    // set dropdown options from the API
                    if (chosOptions) {
                        if (field?.dropdownOptionsAPI == 'chos') {
                            copyDropdownsOptionsApi['chos'] = chosOptions;
                        }
                    }

                    if (gtaGroupsOptions) {
                        if (field?.dropdownOptionsAPI == 'gtaGroups') {
                            copyDropdownsOptionsApi['gtaGroups'] = gtaGroupsOptions;
                        }
                    }
                });
            });
        });

        setDropdownsOptionsApi(copyDropdownsOptionsApi);

    }, [chosOptions, gtaGroupsOptions]);

    useEffect(() => {
        if (saveRulesCategories?.length > 0) {

            saveRulesCategories.forEach((ruleCategoryName) => {
                let searchRuleset = rulesets.find((rule) => rule.name == ruleCategoryName);

                if (searchRuleset) {
                    saveFields(searchRuleset);
                }
            });

            setShowTabSwitchConfirm(false);

            if (newTabEvent) {
                setActiveTabIndex(newTabEvent.index);
                setCurrentTabName(modalTabs[newTabEvent.index].key);
                setUpdateCurrentTabInHMenu(Date.now());
                setNewTabEvent();
            }
        }

    }, [saveRulesCategories]);

    return (
        <RulesetsWrap>
            <div className="custom-content">
                <div className="flex justify-content-between align-items-center">
                    <div className="check-all-wrap">
                        <Checkbox inputId="check-all-rulesets" className="mr-2"
                                  name="checked_rulesets"
                                  value={null}
                                  onChange={onCheckAllRulesets}
                                  checked={checkAllRulesets}
                        />
                        <label htmlFor="check-all-rulesets" className="p-checkbox-label">Select all</label>
                    </div>

                    <div className="flex align-items-center">
                        <span className="font-semibold mr-3 rulesets-info-text">Fields completed: {percent}%</span>
                        <span className="font-semibold mr-3 rulesets-info-text">
                            Rules available to run: {availableRulesNo}/{totalRulesNo}
                        </span>
                        <Button
                            label="Generate Outcome"
                            onClick={() => onGenerateOutcome()}
                            disabled={disabled || !checkedRulesets.length}
                            loading={formLoading || rulesetsLoading}
                            loadingIcon="pi pi-spin pi-sun"
                            iconPos="center"
                        />
                    </div>
                </div>

                <div className="rulesets-wrap">
                    <Accordion activeIndex={activeAccordionIndex} onTabChange={(e) => {

                        if (e.originalEvent.target.classList.contains('p-checkbox-box')
                            || e.originalEvent.target.classList.contains('p-checkbox-icon')) {
                            // || e.originalEvent.target.classList.contains('ruleset-name')) {
                            e.originalEvent.preventDefault();
                            e.originalEvent.stopPropagation();

                        } else {
                            setActiveAccordionIndex(e.index);
                        }

                    }} multiple className="my-5">
                        {rulesets.map((ruleset) => {
                            return (
                                <AccordionTab
                                    headerTemplate={() => headerTemplate(ruleset, Boolean(Math.round(Math.random())), Boolean(Math.round(Math.random())))}
                                    key={ruleset.name + '_' + ruleset.id}>
                                    {generateAccordionContent(ruleset, Boolean(Math.round(Math.random())), Boolean(Math.round(Math.random())))}
                                </AccordionTab>
                            );
                        })}
                        {rulesetsLoading && <ContentLoadingTemplate/>}
                    </Accordion>
                </div>
            </div>

            {currentBRule && <AddDateModal
                visible={showAddDateModal}
                setVisible={setShowAddDateModal}
                ruleset={currentBRule}
                caseId={caseId}
                loadRulesets={loadRulesets}
                reloadCaseData={reloadCaseData}
            />}
            {!!showWarningModal && <ConfirmDialogTemplate
                title={'Reset warning!'}
                message={'This action will regenerate the ISD using new output from the ruleset and overwrite any user amendments to the current ISD - are you sure?'}
                confirmButtonLabel={'Continue processing'}
                declineButtonLabel={'Cancel'}
                onConfirm={submitForm}
                visible={showWarningModal}
                setVisible={setShowWarningModal}
                onHide={() => setShowWarningModal(false)}
            />}

            <hr/>
            <div className="flex justify-content-end align-items-center">
                <span className="font-semibold mr-3 rulesets-info-text">
                    Rules available to run: {availableRulesNo}/{totalRulesNo}
                </span>
                <Button
                    label="Generate Outcome"
                    onClick={() => onGenerateOutcome()}
                    disabled={disabled || !checkedRulesets.length}
                    loading={rulesetsLoading}
                    loadingIcon="pi pi-spin pi-sun"
                    iconPos="center"
                />
            </div>
        </RulesetsWrap>
    );
};

export default Rulesets;
